import { useController, type Control, type UseFormClearErrors } from 'react-hook-form';
import { RadioGroupField, SCALE_1_TO_10, SCALE_1_TO_5 } from './RadioGroupField';
import type {
    FrequencyField,
    NumericScaleField,
    ProgressReportFormValues,
} from '@/types/progress-report-form';
import { FREQUENCY_OPTIONS } from '@/types/progress-reports';

/**
 * The form's answer rows, bound to react-hook-form.
 *
 * Each row owns its own validation rule so the message lives next to the field
 * that produces it, and `useController` keeps the null-until-answered value out
 * of the DOM: an unanswered row has no checked radio at all.
 */

type Ctl = Control<ProgressReportFormValues>;
type ClearErrors = UseFormClearErrors<ProgressReportFormValues>;

export const UNANSWERED_MESSAGE = 'Please choose an answer.';

/**
 * Errors here are set by `trigger` when someone presses Next, and the form runs
 * in the default 'onSubmit' mode, which never re-validates on change because
 * the form has not been submitted. Without this the client answers the question
 * they missed and the red "Please choose an answer." stays put, which reads as
 * "I did it wrong again". The only rule on these rows is `required`, so having
 * a value is enough to clear it — no need to re-run validation.
 */
function useAnswerHandler(
    name: FrequencyField | NumericScaleField,
    hasError: boolean,
    clearErrors: ClearErrors,
    onChange: (value: unknown) => void,
) {
    return (value: unknown) => {
        onChange(value);
        if (hasError) clearErrors(name);
    };
}

/** One of the 19 Likert rows: never / rarely / sometimes / often / always. */
export function LikertRow({
    control,
    name,
    legend,
    clearErrors,
}: {
    control: Ctl;
    name: FrequencyField;
    legend: string;
    clearErrors: ClearErrors;
}) {
    const { field, fieldState } = useController({
        control,
        name,
        rules: { required: UNANSWERED_MESSAGE },
    });

    const handleAnswer = useAnswerHandler(name, !!fieldState.error, clearErrors, field.onChange);

    return (
        <RadioGroupField
            legend={legend}
            name={name}
            options={FREQUENCY_OPTIONS}
            value={field.value ?? null}
            onChange={handleAnswer}
            error={fieldState.error?.message}
            inputRef={field.ref}
            layout="flow"
        />
    );
}

/** A 1-10 or 1-5 scale, as discrete options rather than a slider. */
export function ScaleRow({
    control,
    name,
    legend,
    hint,
    max = 10,
    clearErrors,
}: {
    control: Ctl;
    name: NumericScaleField;
    legend: string;
    hint?: string;
    max?: 5 | 10;
    clearErrors: ClearErrors;
}) {
    const { field, fieldState } = useController({
        control,
        name,
        rules: { required: UNANSWERED_MESSAGE },
    });

    const handleAnswer = useAnswerHandler(name, !!fieldState.error, clearErrors, field.onChange);

    return (
        <RadioGroupField
            legend={legend}
            name={name}
            options={max === 5 ? SCALE_1_TO_5 : SCALE_1_TO_10}
            value={field.value ?? null}
            onChange={handleAnswer}
            error={fieldState.error?.message}
            hint={hint}
            inputRef={field.ref}
        />
    );
}
